// ✅ UNCHANGED import block
import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import SidebarLinkGroup from './SidebarLinkGroup';
import {
  Gauge,
  Bitcoin,
  ArrowLeftRight,
  Mail,
  ShoppingCart,
  Settings,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (arg: boolean) => void;
}

const Sidebar = ({ sidebarOpen, setSidebarOpen }: SidebarProps) => {
  const location = useLocation();
  const { pathname } = location;

  const trigger = useRef<HTMLButtonElement>(null);
  const sidebar = useRef<HTMLDivElement>(null);

  const storedSidebarExpanded = localStorage.getItem('sidebar-expanded');
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded === null ? true : storedSidebarExpanded === 'true'
  );

  useEffect(() => {
    const clickHandler = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!sidebar.current || !trigger.current) return;
      if (!sidebarOpen || sidebar.current.contains(target) || trigger.current.contains(target)) return;
      setSidebarOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  }, [sidebarOpen, setSidebarOpen]);

  useEffect(() => {
    const keyHandler = ({ key }: KeyboardEvent) => {
      if (key === 'Escape') setSidebarOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [setSidebarOpen]);

  useEffect(() => {
    localStorage.setItem('sidebar-expanded', sidebarExpanded.toString());
    document.body.classList.toggle('sidebar-expanded', sidebarExpanded);
  }, [sidebarExpanded]);

  return (
    <>
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-opacity-50 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        ref={sidebar}
        className={`fixed top-0 left-0 z-50 h-[70vh] ${
          sidebarExpanded ? 'w-72' : 'w-24'
        } transform bg-gray-900 text-white shadow-lg transition-all duration-300 ease-in-out lg:static lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <button
          ref={trigger}
          className="absolute top-4 right-4 lg:hidden text-white"
          onClick={() => setSidebarOpen(false)}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="no-scrollbar flex h-full flex-col overflow-y-auto py-6 px-4">
          <div className="hidden lg:flex justify-end mb-4">
            <button
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
              className="p-1 rounded hover:bg-gray-800"
            >
              {sidebarExpanded ? (
                <ChevronLeft className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
            </button>
          </div>

          <nav>
            <h3 className={`mb-4 text-xs font-semibold tracking-wider text-gray-400 ${!sidebarExpanded ? 'hidden' : ''}`}>
              MENU
            </h3>
            <ul className="space-y-2">
              {/* Dashboard */}
              <SidebarLinkGroup activeCondition={pathname.startsWith('/dashboard')}>
                {(handleClick, open) => (
                  <>
                    <NavLink
                      to="/dashboard"
                      className={({ isActive }) =>
                        `group flex items-center rounded-lg px-4 py-2 font-medium transition-all hover:bg-gray-800 ${
                          isActive ? 'border-l-4 border-blue-500 bg-gray-800' : ''
                        }`
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        sidebarExpanded ? handleClick() : setSidebarExpanded(true);
                      }}
                    >
                      <Gauge className="h-5 w-5" />
                      {sidebarExpanded && (
                        <>
                          <span className="ml-3">Dashboard</span>
                          <ChevronDown
                            className={`ml-auto h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
                          />
                        </>
                      )}
                    </NavLink>
                    {sidebarExpanded && open && (
                      <div className="pl-10">
                        <NavLink
                          to="/dashboard"
                          className={({ isActive }) =>
                            `block py-1 text-sm text-gray-300 hover:text-white ${
                              isActive ? 'text-white font-semibold' : ''
                            }`
                          }
                        >
                          Overview
                        </NavLink>
                      </div>
                    )}
                  </>
                )}
              </SidebarLinkGroup>

              {/* Coins */}
              <SidebarLinkGroup activeCondition={pathname.includes('/coins')}>
                {(handleClick, open) => (
                  <>
                    <NavLink
                      to="/dashboard/coins"
                      className={({ isActive }) =>
                        `group flex items-center rounded-lg px-4 py-2 font-medium transition-all hover:bg-gray-800 ${
                          isActive ? 'border-l-4 border-blue-500 bg-gray-800' : ''
                        }`
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        sidebarExpanded ? handleClick() : setSidebarExpanded(true);
                      }}
                    >
                      <Bitcoin className="h-5 w-5" />
                      {sidebarExpanded && (
                        <>
                          <span className="ml-3">Crypto</span>
                          <ChevronDown
                            className={`ml-auto h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
                          />
                        </>
                      )}
                    </NavLink>
                    {sidebarExpanded && open && (
                      <div className="pl-10">
                        <NavLink
                          to="/dashboard/coins"
                          className={({ isActive }) =>
                            `block py-1 text-sm text-gray-300 hover:text-white ${
                              isActive ? 'text-white font-semibold' : ''
                            }`
                          }
                        >
                          Cryptocurrencies
                        </NavLink>
                        <NavLink
                          to="/dashboard/coins/watchlist"
                          className={({ isActive }) =>
                            `block py-1 text-sm text-gray-300 hover:text-white ${
                              isActive ? 'text-white font-semibold' : ''
                            }`
                          }
                        >
                          My Watchlist
                        </NavLink>
                      </div>
                    )}
                  </>
                )}
              </SidebarLinkGroup>

              {/* Simple links */}
              {[
                {
                  to: '/dashboard/profile',
                  icon: ArrowLeftRight,
                  label: 'Crypto Converter',
                },
                {
                  to: '/dashboard/tables',
                  icon: ShoppingCart,
                  label: 'Buy Crypto',
                }, 
                {
                  to: '/dashboard/settings',
                  icon: Settings,
                  label: 'Settings',
                },
              ].map(({ to, icon: Icon, label }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    className={({ isActive }) =>
                      `group flex items-center rounded-lg px-4 py-2 font-medium transition-all hover:bg-gray-800 ${
                        isActive ? 'border-l-4 border-blue-500 bg-gray-800' : ''
                      }`
                    }
                  >
                    <Icon className="h-5 w-5" />
                    {sidebarExpanded && <span className="ml-3">{label}</span>}
                  </NavLink>
                </li>
              ))}

              {/* Contact Us */}
              <SidebarLinkGroup activeCondition={pathname.includes('/forms')}>
                {(handleClick, open) => (
                  <>
                    <NavLink
                      to="/dashboard/forms"
                      className={({ isActive }) =>
                        `group flex items-center rounded-lg px-4 py-2 font-medium transition-all hover:bg-gray-800 ${
                          isActive ? 'border-l-4 border-blue-500 bg-gray-800' : ''
                        }`
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        sidebarExpanded ? handleClick() : setSidebarExpanded(true);
                      }}
                    >
                      <Mail className="h-5 w-5" />
                      {sidebarExpanded && (
                        <>
                          <span className="ml-3">Contact Us</span>
                          <ChevronDown
                            className={`ml-auto h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
                          />
                        </>
                      )}
                    </NavLink>
                    {sidebarExpanded && open && (
                      <div className="pl-10">
                        <NavLink
                          to="/dashboard/forms/form-elements"
                          className={({ isActive }) =>
                            `block py-1 text-sm text-gray-300 hover:text-white ${
                              isActive ? 'text-white font-semibold' : ''
                            }`
                          }
                        >
                          Feedback
                        </NavLink>
                        <NavLink
                          to="/dashboard/forms/form-layout"
                          className={({ isActive }) =>
                            `block py-1 text-sm text-gray-300 hover:text-white ${
                              isActive ? 'text-white font-semibold' : ''
                            }`
                          }
                        >
                          Contact Us
                        </NavLink>
                      </div>
                    )}
                  </>
                )}
              </SidebarLinkGroup>
            </ul>
          </nav>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;


